.Container {
  position: relative;
  width: 100%;
}

.Trigger {
  align-items: center;
  background-color: var(--rv-bg-app);
  border: 1px solid var(--rv-border);
  border-radius: var(--rv-radius-md);
  color: var(--rv-fg-1);
  cursor: pointer;
  display: flex;
  font-family: var(--rv-font-ui);
  font-size: var(--rv-text-sm);
  gap: var(--rv-space-2);
  height: var(--rv-space-10);
  justify-content: space-between;
  line-height: var(--rv-lh-normal);
  outline: none;
  padding: var(--rv-space-2) var(--rv-space-3);
  transition:
    border-color var(--rv-dur-fast) var(--rv-ease-out),
    box-shadow var(--rv-dur-fast) var(--rv-ease-out);
  width: 100%;
}

.Trigger:hover:not(.disabled, .error) {
  border-color: var(--rv-neutral-300);
}

.Trigger:focus-visible {
  border-color: var(--rv-accent);
  box-shadow: var(--rv-shadow-ring-focus);
  outline: none;
}

.Trigger.open {
  border-color: var(--rv-accent);
  box-shadow: var(--rv-shadow-ring-focus);
}

.Trigger.disabled {
  background-color: var(--rv-bg-app-subtle);
  color: var(--rv-fg-4);
  cursor: not-allowed;
  pointer-events: none;
}

.Trigger.error {
  border-color: var(--rv-danger);
}

.Trigger.error:focus-visible,
.Trigger.error.open {
  border-color: var(--rv-danger);
  box-shadow: 0 0 0 3px var(--color-red-500-alpha-25);
}

/* Inline: plain text until opened — for dense surfaces like table cells. */
.Trigger.inline {
  background-color: transparent;
  border-color: transparent;
  height: var(--rv-space-8);
  padding: 0 var(--rv-space-2);
}

.Trigger.inline:hover:not(.disabled, .error, .open) {
  background-color: var(--rv-bg-app-subtle);
  border-color: transparent;
}

.Trigger.inline svg {
  display: none;
}

.Trigger.inline.open,
.Trigger.inline:focus-visible {
  background-color: var(--rv-bg-app);
}

.Trigger.inline.open svg,
.Trigger.inline:focus-visible svg {
  display: block;
}

.Trigger.inline.disabled {
  background-color: transparent;
}

.Value {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.Icon {
  color: var(--rv-fg-3);
  height: var(--rv-icon-sm);
  pointer-events: none;
  transition: transform var(--rv-dur-med) var(--rv-ease-out);
  width: var(--rv-icon-sm);
}

.Icon.rotated {
  transform: rotate(180deg);
}

.Option svg {
  color: var(--rv-accent);
  height: var(--rv-icon-sm);
  width: var(--rv-icon-sm);
}

.Trigger svg {
  color: var(--rv-fg-3);
  height: var(--rv-icon-sm);
  pointer-events: none;
  transition: transform var(--rv-dur-med) var(--rv-ease-out);
  width: var(--rv-icon-sm);
}

.Trigger.open svg {
  transform: rotate(180deg);
}

.OptionsContainer {
  background-color: var(--rv-surface);
  border: 1px solid var(--rv-border);
  border-radius: var(--rv-radius-md);
  box-shadow: var(--rv-shadow-md);
  display: flex;
  flex-direction: column;
  left: var(--dropdown-left, 0);
  max-height: var(--dropdown-height-medium);
  min-width: var(--space-8xl);
  overflow: hidden;
  position: fixed;
  top: var(--dropdown-top, 100%);
  width: var(--dropdown-width, 100%);
  z-index: var(--rv-z-popover);
}

.OptionsContainer.flipped {
  bottom: var(--dropdown-bottom);
  top: unset;
}

.Options {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: var(--rv-space-1);
}

.Option {
  align-items: center;
  border-radius: var(--rv-radius-sm);
  color: var(--rv-fg-1);
  cursor: pointer;
  display: flex;
  font-family: var(--rv-font-ui);
  font-size: var(--rv-text-sm);
  gap: var(--rv-space-2);
  justify-content: space-between;
  line-height: var(--rv-lh-normal);
  padding: var(--rv-space-2) var(--rv-space-3);
  transition: background-color var(--rv-dur-fast) var(--rv-ease-out);
  user-select: none;
}

.Option:hover:not(.disabled),
.Option.highlighted {
  background-color: var(--rv-bg-app-subtle);
}

.Option.selected {
  background-color: var(--mint-alpha-12);
  color: var(--rv-accent-ink-deep);
}

html[data-color-mode='dark'] .Option.selected {
  color: var(--rv-accent);
}

.Option.disabled {
  cursor: not-allowed;
  opacity: 50%;
  pointer-events: none;
}

.OptionLabel {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.CheckIcon {
  color: var(--rv-accent);
  height: var(--rv-icon-sm);
  width: var(--rv-icon-sm);
}

@media (--max-md) {
  .OptionsContainer {
    max-height: 200px;
  }

}

.Spinner {
  animation: spin 1s linear infinite;
  border: 2px solid var(--rv-border);
  border-radius: 50%;
  border-top: 2px solid var(--rv-fg-3);
  height: var(--rv-icon-sm);
  pointer-events: none;
  width: var(--rv-icon-sm);
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* Search functionality styles */

.SearchContainer {
  border-bottom: 1px solid var(--rv-border);
  flex-shrink: 0;
  padding: var(--rv-space-2);
}

.SearchInputWrapper {
  align-items: center;
  display: flex;
  position: relative;
}

.SearchIcon {
  color: var(--rv-fg-3);
  height: var(--rv-icon-sm);
  left: var(--rv-space-3);
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: var(--rv-icon-sm);
}

.SearchInput {
  background-color: var(--rv-bg-app-subtle);
  border: 1px solid var(--rv-border);
  border-radius: var(--rv-radius-md);
  color: var(--rv-fg-1);
  font-family: var(--rv-font-ui);
  font-size: var(--rv-text-sm);
  line-height: var(--rv-lh-normal);
  outline: none;
  padding: var(--rv-space-2) var(--rv-space-3) var(--rv-space-2)
    calc(var(--rv-space-3) + var(--rv-icon-sm) + var(--rv-space-2));
  transition:
    border-color var(--rv-dur-fast) var(--rv-ease-out),
    box-shadow var(--rv-dur-fast) var(--rv-ease-out);
  width: 100%;
}

.SearchInput:focus {
  border-color: var(--rv-accent);
  box-shadow: var(--rv-shadow-ring-focus);
}

.SearchInput::placeholder {
  color: var(--rv-fg-4);
}

.NoResults {
  color: var(--rv-fg-3);
  font-family: var(--rv-font-ui);
  font-size: var(--rv-text-sm);
  padding: var(--rv-space-3);
  text-align: center;
}
